import {
  type CaptureItem,
  type ConsistencyEvidence,
  type EntityId,
  type Habit,
  type HabitCheckin,
  type HabitStatus,
  type Problem,
  type ProblemPatch,
  type ProblemStatus,
  type ControlCategory,
  type LimiterType,
  type Project,
  type ProjectPatch,
  type ProjectStatus,
  type CalendarEvent,
  type EventPatch,
  type EventStatus,
  type Recurrence,
  type Account,
  type AccountType,
  type Transaction,
  type TransactionKind,
  type ImportBatch,
  type Note,
  type NotePatch,
  type Goal,
  type GoalHorizon,
  type GoalPatch,
  type GoalStatus,
  type Milestone,
  type MilestoneStatus,
  type Pillar,
  type DirectionItem,
  type DirectionLens,
  type HealthLog,
  type StudySubject,
  type StudySession,
  type MasteryState,
  type SubjectStatus,
  type Opportunity,
  type OpportunityPatch,
  type OpportunityStage,
  type JournalEntry,
  type JournalKind,
  type JournalStream,
  type ContentItem,
  type ContentItemPatch,
  type ContentStage,
  type Venture,
  type VenturePatch,
  type VentureStatus,
  type InventoryItem,
  type InventoryItemPatch,
  type ItemCategory,
  type ItemStatus,
  type FocusSession,
  type Profile,
  type EducationRecord,
  type Admission,
  type OfferStatus,
  type AdmissionStatus,
  type FundingSource,
  type FundingKind,
  type FundingStatus,
  type TouchpointChannel,
  type Touchpoint,
  type EducationProgram,
  type EducationProgramInput,
  type ResumeProfile,
  type ResumeProfileInput,
  type SecurityConfig,
  type SecurityEvent,
  type SecurityEventType,
  type Reminder,
  type Review,
  type ReviewOutcome,
  type Routine,
  type RoutineDayType,
  type RoutineStep,
  type SourceRecord,
  type SourceRecordId,
  type SourceReviewState,
  type Target,
  type TargetCadence,
  type TargetStatus,
  type TargetType,
  type Task,
  type TaskLane,
  type WeekdayMask,
  isLaneConsistent,
  newSourceRecordId,
  redactByDefault,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';
import { bumpEnvelope, createEnvelope } from './envelope.js';
import {
  type CaptureRepository,
  type ReminderRepository,
  type ReviewRepository,
  type RoutineRepository,
  type ProblemRepository,
  type ProjectRepository,
  type EventRepository,
  type FinanceRepository,
  type NoteRepository,
  type DirectionRepository,
  type HealthRepository,
  type StudyRepository,
  type CareerRepository,
  type JournalRepository,
  type ContentRepository,
  type BusinessRepository,
  type EnvironmentRepository,
  type FocusRepository,
  type SecurityRepository,
  type SourceRepository,
  type TargetRepository,
  type TaskRepository,
  type AcademicsRepository,
  InvalidTransitionError,
  RecordNotFoundError,
} from './repositories.js';

/** Small, stable, dependency-free content hash (FNV-1a, 32-bit) for dedup/integrity. */
function checksum(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

const dayKey = (iso: string): string => iso.slice(0, 10);

export class MemorySourceRepository implements SourceRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async preserve(input: {
    ownerId: string;
    privacyClass: SourceRecord['privacyClass'];
    origin: SourceRecord['origin'];
    rawBody: string;
    originalPath?: string | null;
  }): Promise<SourceRecord> {
    const id = newSourceRecordId();
    const record: SourceRecord = {
      id,
      ownerId: input.ownerId,
      privacyClass: input.privacyClass,
      origin: input.origin,
      rawBody: input.rawBody,
      checksum: checksum(input.rawBody),
      originalPath: input.originalPath ?? null,
      createdAt: this.clock.nowIso(),
      reviewState: 'preserved',
      promotedToIds: [],
    };
    await this.store.put(Collections.sourceRecords, String(id), record);
    return record;
  }

  async get(id: SourceRecordId): Promise<SourceRecord | undefined> {
    return this.store.get<SourceRecord>(Collections.sourceRecords, String(id));
  }

  async appendPromotion(id: SourceRecordId, entityId: EntityId): Promise<SourceRecord> {
    const existing = await this.get(id);
    if (!existing) throw new RecordNotFoundError('SourceRecord', String(id));
    // rawBody + checksum stay immutable; only review state + links evolve.
    const updated: SourceRecord = {
      ...existing,
      reviewState: 'promoted',
      promotedToIds: [...existing.promotedToIds, entityId],
    };
    await this.store.put(Collections.sourceRecords, String(id), updated);
    return updated;
  }

  async setReviewState(id: SourceRecordId, state: SourceReviewState): Promise<SourceRecord> {
    const existing = await this.get(id);
    if (!existing) throw new RecordNotFoundError('SourceRecord', String(id));
    const updated: SourceRecord = { ...existing, reviewState: state };
    await this.store.put(Collections.sourceRecords, String(id), updated);
    return updated;
  }

  async list(): Promise<readonly SourceRecord[]> {
    return this.store.list<SourceRecord>(Collections.sourceRecords);
  }
}

export class MemoryCaptureRepository implements CaptureRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async create(args: {
    ownerId: string;
    rawText: string;
    captureType: CaptureItem['captureType'];
    privacyClass: CaptureItem['privacyClass'];
    sourceRecordId: SourceRecordId;
  }): Promise<CaptureItem> {
    const env = createEnvelope(
      {
        kind: 'capture_item',
        ownerId: args.ownerId,
        privacyClass: args.privacyClass,
        sourceRecordId: args.sourceRecordId,
      },
      this.clock,
    );
    const item: CaptureItem = {
      ...env,
      kind: 'capture_item',
      rawText: args.rawText,
      captureType: args.captureType,
      reviewState: 'unreviewed',
      reviewOutcome: null,
    };
    await this.store.put(Collections.captureItems, String(item.id), item);
    return item;
  }

  async get(id: EntityId): Promise<CaptureItem | undefined> {
    return this.store.get<CaptureItem>(Collections.captureItems, String(id));
  }

  async listUnreviewed(): Promise<readonly CaptureItem[]> {
    const all = await this.store.list<CaptureItem>(Collections.captureItems);
    return all.filter((c) => c.reviewState === 'unreviewed');
  }

  async markReviewed(id: EntityId, outcome: ReviewOutcome): Promise<CaptureItem> {
    const existing = await this.get(id);
    if (!existing) throw new RecordNotFoundError('CaptureItem', String(id));
    if (existing.reviewState !== 'unreviewed') {
      throw new InvalidTransitionError(`capture ${String(id)} already reviewed`);
    }
    const updated: CaptureItem = {
      ...bumpEnvelope(existing, this.clock),
      reviewState: 'reviewed',
      reviewOutcome: outcome,
    };
    await this.store.put(Collections.captureItems, String(id), updated);
    return updated;
  }
}

export class MemoryTaskRepository implements TaskRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async create(args: {
    ownerId: string;
    title: string;
    lane: TaskLane;
    scheduledFor: string | null;
    privacyClass: Task['privacyClass'];
    links: Task['links'];
  }): Promise<Task> {
    const scheduledFor = args.lane === 'scheduled' ? args.scheduledFor : null;
    const env = createEnvelope(
      { kind: 'task', ownerId: args.ownerId, privacyClass: args.privacyClass },
      this.clock,
    );
    const task: Task = {
      ...env,
      kind: 'task',
      title: args.title,
      lane: args.lane,
      scheduledFor,
      links: args.links,
      reviewState: 'open',
    };
    if (!isLaneConsistent(task)) {
      throw new InvalidTransitionError(`lane ${task.lane} requires scheduledFor consistency`);
    }
    await this.store.put(Collections.tasks, String(task.id), task);
    return task;
  }

  async get(id: EntityId): Promise<Task | undefined> {
    return this.store.get<Task>(Collections.tasks, String(id));
  }

  async listByLane(lane: TaskLane): Promise<readonly Task[]> {
    const all = await this.store.list<Task>(Collections.tasks);
    return all.filter((t) => t.lane === lane && t.lifecycle === 'active');
  }

  async listScheduledOn(dateIso: string): Promise<readonly Task[]> {
    const all = await this.store.list<Task>(Collections.tasks);
    const key = dayKey(dateIso);
    return all.filter(
      (t) => t.lane === 'scheduled' && t.scheduledFor !== null && dayKey(t.scheduledFor) === key,
    );
  }

  async move(id: EntityId, lane: TaskLane, scheduledFor: string | null): Promise<Task> {
    const existing = await this.get(id);
    if (!existing) throw new RecordNotFoundError('Task', String(id));
    const nextScheduled = lane === 'scheduled' ? scheduledFor : null;
    const updated: Task = { ...bumpEnvelope(existing, this.clock), lane, scheduledFor: nextScheduled };
    if (!isLaneConsistent(updated)) {
      throw new InvalidTransitionError(`lane ${lane} requires a scheduledFor time`);
    }
    await this.store.put(Collections.tasks, String(id), updated);
    return updated;
  }

  async complete(id: EntityId): Promise<Task> {
    const existing = await this.get(id);
    if (!existing) throw new RecordNotFoundError('Task', String(id));
    const updated: Task = {
      ...bumpEnvelope(existing, this.clock),
      lane: 'done',
      scheduledFor: null,
      reviewState: 'reviewed',
    };
    await this.store.put(Collections.tasks, String(id), updated);
    return updated;
  }
}

export class MemoryReviewRepository implements ReviewRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async create(args: {
    ownerId: string;
    reviewType: Review['reviewType'];
    forDate: string;
    notes: string;
    touchedIds: readonly EntityId[];
  }): Promise<Review> {
    const env = createEnvelope(
      { kind: 'review', ownerId: args.ownerId, privacyClass: 'sensitive_private' },
      this.clock,
    );
    const review: Review = {
      ...env,
      kind: 'review',
      reviewType: args.reviewType,
      forDate: args.forDate,
      notes: args.notes,
      touchedIds: args.touchedIds,
    };
    await this.store.put(Collections.reviews, String(review.id), review);
    return review;
  }

  async listForDate(dateIso: string): Promise<readonly Review[]> {
    const all = await this.store.list<Review>(Collections.reviews);
    const key = dayKey(dateIso);
    return all.filter((r) => dayKey(r.forDate) === key);
  }

  async listRecent(limit: number): Promise<readonly Review[]> {
    const all = await this.store.list<Review>(Collections.reviews);
    return [...all].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, limit);
  }
}

export class MemoryReminderRepository implements ReminderRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async create(args: {
    ownerId: string;
    label: string;
    linkedId: EntityId | null;
    fireAt: string;
    privacyClass: Reminder['privacyClass'];
  }): Promise<Reminder> {
    const env = createEnvelope(
      { kind: 'reminder', ownerId: args.ownerId, privacyClass: args.privacyClass },
      this.clock,
    );
    const reminder: Reminder = {
      ...env,
      kind: 'reminder',
      label: args.label,
      linkedId: args.linkedId,
      fireAt: args.fireAt,
      state: 'scheduled',
      redactByDefault: redactByDefault(args.privacyClass),
    };
    await this.store.put(Collections.reminders, String(reminder.id), reminder);
    return reminder;
  }

  async listFiringOn(dateIso: string): Promise<readonly Reminder[]> {
    const all = await this.store.list<Reminder>(Collections.reminders);
    const key = dayKey(dateIso);
    return all.filter((r) => dayKey(r.fireAt) === key && r.state === 'scheduled');
  }

  async listAll(): Promise<readonly Reminder[]> {
    const all = await this.store.list<Reminder>(Collections.reminders);
    return [...all].sort((a, b) => a.fireAt.localeCompare(b.fireAt));
  }

  async setState(id: EntityId, state: Reminder['state']): Promise<Reminder> {
    const existing = await this.store.get<Reminder>(Collections.reminders, String(id));
    if (!existing) throw new RecordNotFoundError('Reminder', String(id));
    const updated: Reminder = { ...bumpEnvelope(existing, this.clock), state };
    await this.store.put(Collections.reminders, String(id), updated);
    return updated;
  }
}

export class MemoryTargetRepository implements TargetRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createTarget(args: {
    ownerId: string;
    title: string;
    targetType: TargetType;
    cadence: TargetCadence;
    quantity: number;
    qualityBar: string;
    evidenceRule: string;
    minimumViableAction: string;
    privacyClass: Target['privacyClass'];
  }): Promise<Target> {
    const env = createEnvelope(
      { kind: 'target', ownerId: args.ownerId, privacyClass: args.privacyClass },
      this.clock,
    );
    const target: Target = {
      ...env,
      kind: 'target',
      title: args.title,
      targetType: args.targetType,
      cadence: args.cadence,
      quantity: args.quantity,
      qualityBar: args.qualityBar,
      evidenceRule: args.evidenceRule,
      minimumViableAction: args.minimumViableAction,
      status: 'active',
      linkedIds: [],
    };
    await this.store.put(Collections.targets, String(target.id), target);
    return target;
  }

  async getTarget(id: EntityId): Promise<Target | undefined> {
    return this.store.get<Target>(Collections.targets, String(id));
  }

  async listTargets(): Promise<readonly Target[]> {
    const all = await this.store.list<Target>(Collections.targets);
    return all.filter((t) => t.lifecycle === 'active');
  }

  async setStatus(id: EntityId, status: TargetStatus): Promise<Target> {
    const existing = await this.getTarget(id);
    if (!existing) throw new RecordNotFoundError('Target', String(id));
    const updated: Target = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.targets, String(id), updated);
    return updated;
  }

  async addEvidence(args: {
    ownerId: string;
    targetId: EntityId;
    occurredOn: string;
    note: string;
    privacyClass: Target['privacyClass'];
  }): Promise<ConsistencyEvidence> {
    const target = await this.getTarget(args.targetId);
    if (!target) throw new RecordNotFoundError('Target', String(args.targetId));
    const env = createEnvelope(
      { kind: 'consistency_evidence', ownerId: args.ownerId, privacyClass: args.privacyClass },
      this.clock,
    );
    const evidence: ConsistencyEvidence = {
      ...env,
      kind: 'consistency_evidence',
      targetId: args.targetId,
      occurredOn: args.occurredOn,
      note: args.note,
    };
    await this.store.put(Collections.evidence, String(evidence.id), evidence);
    return evidence;
  }

  async listEvidence(targetId: EntityId): Promise<readonly ConsistencyEvidence[]> {
    const all = await this.store.list<ConsistencyEvidence>(Collections.evidence);
    return all.filter((e) => String(e.targetId) === String(targetId));
  }
}

export class MemoryRoutineRepository implements RoutineRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createHabit(args: {
    ownerId: string;
    title: string;
    weekdayMask: WeekdayMask;
    privacyClass: Habit['privacyClass'];
  }): Promise<Habit> {
    const env = createEnvelope({ kind: 'habit', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const habit: Habit = {
      ...env,
      kind: 'habit',
      title: args.title,
      weekdayMask: args.weekdayMask,
      status: 'active',
      linkedIds: [],
    };
    await this.store.put(Collections.habits, String(habit.id), habit);
    return habit;
  }

  async getHabit(id: EntityId): Promise<Habit | undefined> {
    return this.store.get<Habit>(Collections.habits, String(id));
  }

  async listHabits(): Promise<readonly Habit[]> {
    const all = await this.store.list<Habit>(Collections.habits);
    return all.filter((h) => h.lifecycle === 'active');
  }

  async setHabitStatus(id: EntityId, status: HabitStatus): Promise<Habit> {
    const existing = await this.getHabit(id);
    if (!existing) throw new RecordNotFoundError('Habit', String(id));
    const updated: Habit = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.habits, String(id), updated);
    return updated;
  }

  async addCheckin(args: {
    ownerId: string;
    habitId: EntityId;
    occurredOn: string;
    note: string;
    privacyClass: Habit['privacyClass'];
  }): Promise<HabitCheckin> {
    const habit = await this.getHabit(args.habitId);
    if (!habit) throw new RecordNotFoundError('Habit', String(args.habitId));
    const env = createEnvelope(
      { kind: 'habit_checkin', ownerId: args.ownerId, privacyClass: args.privacyClass },
      this.clock,
    );
    const checkin: HabitCheckin = {
      ...env,
      kind: 'habit_checkin',
      habitId: args.habitId,
      occurredOn: args.occurredOn,
      note: args.note,
    };
    await this.store.put(Collections.habitCheckins, String(checkin.id), checkin);
    return checkin;
  }

  async listCheckins(habitId: EntityId): Promise<readonly HabitCheckin[]> {
    const all = await this.store.list<HabitCheckin>(Collections.habitCheckins);
    return all.filter((c) => String(c.habitId) === String(habitId));
  }

  async createRoutine(args: {
    ownerId: string;
    title: string;
    dayType: RoutineDayType;
    weekdayMask: WeekdayMask;
    steps: readonly RoutineStep[];
    privacyClass: Routine['privacyClass'];
  }): Promise<Routine> {
    const env = createEnvelope({ kind: 'routine', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const routine: Routine = {
      ...env,
      kind: 'routine',
      title: args.title,
      dayType: args.dayType,
      weekdayMask: args.weekdayMask,
      steps: args.steps,
      status: 'active',
      lastRanAt: null,
    };
    await this.store.put(Collections.routines, String(routine.id), routine);
    return routine;
  }

  async getRoutine(id: EntityId): Promise<Routine | undefined> {
    return this.store.get<Routine>(Collections.routines, String(id));
  }

  async listRoutines(): Promise<readonly Routine[]> {
    const all = await this.store.list<Routine>(Collections.routines);
    return all.filter((r) => r.lifecycle === 'active');
  }

  async markRan(id: EntityId, ranAtIso: string): Promise<Routine> {
    const existing = await this.getRoutine(id);
    if (!existing) throw new RecordNotFoundError('Routine', String(id));
    const updated: Routine = { ...bumpEnvelope(existing, this.clock), lastRanAt: ranAtIso };
    await this.store.put(Collections.routines, String(id), updated);
    return updated;
  }

  async setRoutineStatus(id: EntityId, status: HabitStatus): Promise<Routine> {
    const existing = await this.getRoutine(id);
    if (!existing) throw new RecordNotFoundError('Routine', String(id));
    const updated: Routine = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.routines, String(id), updated);
    return updated;
  }
}

export class MemoryProblemRepository implements ProblemRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createProblem(args: {
    ownerId: string;
    title: string;
    controlCategory: ControlCategory;
    limiterType: LimiterType;
    cause: string;
    intervention: string;
    nextStep: string;
    privacyClass: Problem['privacyClass'];
  }): Promise<Problem> {
    const env = createEnvelope({ kind: 'problem', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const problem: Problem = {
      ...env,
      kind: 'problem',
      title: args.title,
      controlCategory: args.controlCategory,
      limiterType: args.limiterType,
      status: 'identified',
      cause: args.cause,
      intervention: args.intervention,
      nextStep: args.nextStep,
      linkedIds: [],
    };
    await this.store.put(Collections.problems, String(problem.id), problem);
    return problem;
  }

  async getProblem(id: EntityId): Promise<Problem | undefined> {
    return this.store.get<Problem>(Collections.problems, String(id));
  }

  async listProblems(): Promise<readonly Problem[]> {
    const all = await this.store.list<Problem>(Collections.problems);
    return all.filter((p) => p.lifecycle === 'active');
  }

  async updateProblem(id: EntityId, patch: ProblemPatch): Promise<Problem> {
    const existing = await this.getProblem(id);
    if (!existing) throw new RecordNotFoundError('Problem', String(id));
    const updated: Problem = {
      ...bumpEnvelope(existing, this.clock),
      controlCategory: patch.controlCategory ?? existing.controlCategory,
      limiterType: patch.limiterType ?? existing.limiterType,
      cause: patch.cause ?? existing.cause,
      intervention: patch.intervention ?? existing.intervention,
      nextStep: patch.nextStep ?? existing.nextStep,
    };
    await this.store.put(Collections.problems, String(id), updated);
    return updated;
  }

  async setStatus(id: EntityId, status: ProblemStatus): Promise<Problem> {
    const existing = await this.getProblem(id);
    if (!existing) throw new RecordNotFoundError('Problem', String(id));
    const updated: Problem = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.problems, String(id), updated);
    return updated;
  }
}

export class MemoryProjectRepository implements ProjectRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createProject(args: {
    ownerId: string;
    title: string;
    summary: string;
    nextAction: string;
    definitionOfDone: string;
    privacyClass: Project['privacyClass'];
  }): Promise<Project> {
    const env = createEnvelope({ kind: 'project', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const project: Project = {
      ...env,
      kind: 'project',
      title: args.title,
      summary: args.summary,
      status: 'idea',
      nextAction: args.nextAction,
      definitionOfDone: args.definitionOfDone,
      linkedIds: [],
    };
    await this.store.put(Collections.projects, String(project.id), project);
    return project;
  }

  async getProject(id: EntityId): Promise<Project | undefined> {
    return this.store.get<Project>(Collections.projects, String(id));
  }

  async listProjects(): Promise<readonly Project[]> {
    const all = await this.store.list<Project>(Collections.projects);
    return all.filter((p) => p.lifecycle === 'active');
  }

  async updateProject(id: EntityId, patch: ProjectPatch): Promise<Project> {
    const existing = await this.getProject(id);
    if (!existing) throw new RecordNotFoundError('Project', String(id));
    const updated: Project = {
      ...bumpEnvelope(existing, this.clock),
      summary: patch.summary ?? existing.summary,
      nextAction: patch.nextAction ?? existing.nextAction,
      definitionOfDone: patch.definitionOfDone ?? existing.definitionOfDone,
    };
    await this.store.put(Collections.projects, String(id), updated);
    return updated;
  }

  async setStatus(id: EntityId, status: ProjectStatus): Promise<Project> {
    const existing = await this.getProject(id);
    if (!existing) throw new RecordNotFoundError('Project', String(id));
    const updated: Project = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.projects, String(id), updated);
    return updated;
  }
}

export class MemoryEventRepository implements EventRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createEvent(args: {
    ownerId: string;
    title: string;
    startsAt: string;
    durationMinutes: number;
    location: string;
    recurrence: Recurrence;
    privacyClass: CalendarEvent['privacyClass'];
  }): Promise<CalendarEvent> {
    const env = createEnvelope({ kind: 'event', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const event: CalendarEvent = {
      ...env,
      kind: 'event',
      title: args.title,
      startsAt: args.startsAt,
      durationMinutes: args.durationMinutes,
      location: args.location,
      recurrence: args.recurrence,
      status: 'confirmed',
      linkedIds: [],
    };
    await this.store.put(Collections.calendarEvents, String(event.id), event);
    return event;
  }

  async getEvent(id: EntityId): Promise<CalendarEvent | undefined> {
    return this.store.get<CalendarEvent>(Collections.calendarEvents, String(id));
  }

  async listEvents(): Promise<readonly CalendarEvent[]> {
    const all = await this.store.list<CalendarEvent>(Collections.calendarEvents);
    return all.filter((e) => e.lifecycle === 'active');
  }

  async updateEvent(id: EntityId, patch: EventPatch): Promise<CalendarEvent> {
    const existing = await this.getEvent(id);
    if (!existing) throw new RecordNotFoundError('CalendarEvent', String(id));
    const updated: CalendarEvent = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      startsAt: patch.startsAt ?? existing.startsAt,
      durationMinutes: patch.durationMinutes ?? existing.durationMinutes,
      location: patch.location ?? existing.location,
      recurrence: patch.recurrence ?? existing.recurrence,
    };
    await this.store.put(Collections.calendarEvents, String(id), updated);
    return updated;
  }

  async setStatus(id: EntityId, status: EventStatus): Promise<CalendarEvent> {
    const existing = await this.getEvent(id);
    if (!existing) throw new RecordNotFoundError('CalendarEvent', String(id));
    const updated: CalendarEvent = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.calendarEvents, String(id), updated);
    return updated;
  }
}

export class MemoryFinanceRepository implements FinanceRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createAccount(args: {
    ownerId: string;
    name: string;
    accountType: AccountType;
    currency: string;
    openingBalanceMinor: number;
    privacyClass: Account['privacyClass'];
  }): Promise<Account> {
    const env = createEnvelope({ kind: 'account', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const account: Account = {
      ...env,
      kind: 'account',
      name: args.name,
      accountType: args.accountType,
      currency: args.currency,
      openingBalanceMinor: Math.trunc(args.openingBalanceMinor),
    };
    await this.store.put(Collections.accounts, String(account.id), account);
    return account;
  }

  async getAccount(id: EntityId): Promise<Account | undefined> {
    return this.store.get<Account>(Collections.accounts, String(id));
  }

  async listAccounts(): Promise<readonly Account[]> {
    const all = await this.store.list<Account>(Collections.accounts);
    return all.filter((a) => a.lifecycle === 'active');
  }

  async createTransaction(args: {
    ownerId: string;
    accountId: EntityId;
    txnKind: TransactionKind;
    amountMinor: number;
    category: string;
    occurredOn: string;
    note: string;
    occurredAt: string | null;
    counterparty: string | null;
    externalReference: string | null;
    rail: Transaction['rail'];
    balanceAfterMinor: number | null;
    importFingerprint: string | null;
    importBatchId: EntityId | null;
    privacyClass: Transaction['privacyClass'];
  }): Promise<Transaction> {
    const account = await this.getAccount(args.accountId);
    if (!account) throw new RecordNotFoundError('Account', String(args.accountId));
    const env = createEnvelope({ kind: 'transaction', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const txn: Transaction = {
      ...env,
      kind: 'transaction',
      accountId: args.accountId,
      txnKind: args.txnKind,
      amountMinor: Math.max(0, Math.trunc(args.amountMinor)),
      category: args.category,
      occurredOn: args.occurredOn,
      note: args.note,
      occurredAt: args.occurredAt,
      counterparty: args.counterparty,
      externalReference: args.externalReference,
      rail: args.rail,
      balanceAfterMinor: args.balanceAfterMinor,
      importFingerprint: args.importFingerprint,
      importBatchId: args.importBatchId,
    };
    await this.store.put(Collections.transactions, String(txn.id), txn);
    return txn;
  }

  async listTransactions(accountId?: EntityId): Promise<readonly Transaction[]> {
    const all = await this.store.list<Transaction>(Collections.transactions);
    const active = all.filter((t) => t.lifecycle === 'active');
    if (!accountId) return active;
    return active.filter((t) => String(t.accountId) === String(accountId));
  }

  async createImportBatch(args: {
    ownerId: string;
    accountId: EntityId;
    sourceLabel: string;
    sourceFiles: readonly string[];
    rowsSeen: number;
    rowsCreated: number;
    rowsSkipped: number;
    importedOn: string;
    note: string;
    privacyClass: ImportBatch['privacyClass'];
  }): Promise<ImportBatch> {
    const env = createEnvelope({ kind: 'import_batch', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const batch: ImportBatch = {
      ...env,
      kind: 'import_batch',
      accountId: args.accountId,
      sourceLabel: args.sourceLabel,
      sourceFiles: args.sourceFiles,
      rowsSeen: args.rowsSeen,
      rowsCreated: args.rowsCreated,
      rowsSkipped: args.rowsSkipped,
      importedOn: args.importedOn,
      note: args.note,
    };
    await this.store.put(Collections.importBatches, String(batch.id), batch);
    return batch;
  }

  async listImportBatches(): Promise<readonly ImportBatch[]> {
    const all = await this.store.list<ImportBatch>(Collections.importBatches);
    return all.filter((b) => b.lifecycle === 'active');
  }
}

export class MemoryNoteRepository implements NoteRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createNote(args: {
    ownerId: string;
    title: string;
    blocks: Note['blocks'];
    tags: readonly string[];
    privacyClass: Note['privacyClass'];
  }): Promise<Note> {
    const env = createEnvelope({ kind: 'note', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const note: Note = {
      ...env,
      kind: 'note',
      title: args.title,
      blocks: args.blocks,
      tags: args.tags,
    };
    await this.store.put(Collections.notes, String(note.id), note);
    return note;
  }

  async getNote(id: EntityId): Promise<Note | undefined> {
    return this.store.get<Note>(Collections.notes, String(id));
  }

  async listNotes(): Promise<readonly Note[]> {
    const all = await this.store.list<Note>(Collections.notes);
    return all.filter((n) => n.lifecycle === 'active');
  }

  async updateNote(id: EntityId, patch: NotePatch): Promise<Note> {
    const existing = await this.getNote(id);
    if (!existing) throw new RecordNotFoundError('Note', String(id));
    const updated: Note = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      blocks: patch.blocks ?? existing.blocks,
      tags: patch.tags ?? existing.tags,
    };
    await this.store.put(Collections.notes, String(id), updated);
    return updated;
  }

  async archiveNote(id: EntityId): Promise<Note> {
    const existing = await this.getNote(id);
    if (!existing) throw new RecordNotFoundError('Note', String(id));
    // No deletion: flip lifecycle to archived, preserving the record + history.
    const updated: Note = { ...bumpEnvelope(existing, this.clock), lifecycle: 'archived' };
    await this.store.put(Collections.notes, String(id), updated);
    return updated;
  }
}

export class MemoryDirectionRepository implements DirectionRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createGoal(args: {
    ownerId: string;
    title: string;
    pillar: Pillar;
    horizon: GoalHorizon;
    why: string;
    privacyClass: Goal['privacyClass'];
  }): Promise<Goal> {
    const env = createEnvelope({ kind: 'goal', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const goal: Goal = {
      ...env,
      kind: 'goal',
      title: args.title,
      pillar: args.pillar,
      horizon: args.horizon,
      why: args.why,
      status: 'active',
      linkedIds: [],
    };
    await this.store.put(Collections.goals, String(goal.id), goal);
    return goal;
  }

  async getGoal(id: EntityId): Promise<Goal | undefined> {
    return this.store.get<Goal>(Collections.goals, String(id));
  }

  async listGoals(): Promise<readonly Goal[]> {
    const all = await this.store.list<Goal>(Collections.goals);
    return all.filter((g) => g.lifecycle === 'active');
  }

  async updateGoal(id: EntityId, patch: GoalPatch): Promise<Goal> {
    const existing = await this.getGoal(id);
    if (!existing) throw new RecordNotFoundError('Goal', String(id));
    const updated: Goal = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      pillar: patch.pillar ?? existing.pillar,
      horizon: patch.horizon ?? existing.horizon,
      why: patch.why ?? existing.why,
    };
    await this.store.put(Collections.goals, String(id), updated);
    return updated;
  }

  async setGoalStatus(id: EntityId, status: GoalStatus): Promise<Goal> {
    const existing = await this.getGoal(id);
    if (!existing) throw new RecordNotFoundError('Goal', String(id));
    const updated: Goal = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.goals, String(id), updated);
    return updated;
  }

  async createMilestone(args: {
    ownerId: string;
    title: string;
    contributesToGoalIds: readonly EntityId[];
    privacyClass: Milestone['privacyClass'];
  }): Promise<Milestone> {
    const env = createEnvelope({ kind: 'milestone', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const milestone: Milestone = {
      ...env,
      kind: 'milestone',
      title: args.title,
      status: 'open',
      contributesToGoalIds: args.contributesToGoalIds,
      linkedIds: [],
    };
    await this.store.put(Collections.milestones, String(milestone.id), milestone);
    return milestone;
  }

  async getMilestone(id: EntityId): Promise<Milestone | undefined> {
    return this.store.get<Milestone>(Collections.milestones, String(id));
  }

  async listMilestones(): Promise<readonly Milestone[]> {
    const all = await this.store.list<Milestone>(Collections.milestones);
    return all.filter((m) => m.lifecycle === 'active');
  }

  async setMilestoneStatus(id: EntityId, status: MilestoneStatus): Promise<Milestone> {
    const existing = await this.getMilestone(id);
    if (!existing) throw new RecordNotFoundError('Milestone', String(id));
    const updated: Milestone = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.milestones, String(id), updated);
    return updated;
  }

  async setMilestoneGoals(id: EntityId, goalIds: readonly EntityId[]): Promise<Milestone> {
    const existing = await this.getMilestone(id);
    if (!existing) throw new RecordNotFoundError('Milestone', String(id));
    const updated: Milestone = { ...bumpEnvelope(existing, this.clock), contributesToGoalIds: goalIds };
    await this.store.put(Collections.milestones, String(id), updated);
    return updated;
  }

  async createItem(args: {
    ownerId: string;
    lens: DirectionLens;
    itemKind: DirectionItem['itemKind'];
    title: string;
    detail: string;
    posture: ControlCategory | null;
    privacyClass: DirectionItem['privacyClass'];
  }): Promise<DirectionItem> {
    const env = createEnvelope({ kind: 'direction_item', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const item: DirectionItem = {
      ...env,
      kind: 'direction_item',
      lens: args.lens,
      itemKind: args.itemKind,
      title: args.title,
      detail: args.detail,
      posture: args.posture,
      linkedIds: [],
    };
    await this.store.put(Collections.directionItems, String(item.id), item);
    return item;
  }

  async getItem(id: EntityId): Promise<DirectionItem | undefined> {
    return this.store.get<DirectionItem>(Collections.directionItems, String(id));
  }

  async listItems(lens: DirectionLens): Promise<readonly DirectionItem[]> {
    const all = await this.store.list<DirectionItem>(Collections.directionItems);
    return all.filter((i) => i.lifecycle === 'active' && i.lens === lens);
  }

  async setItemPosture(id: EntityId, posture: ControlCategory | null): Promise<DirectionItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('DirectionItem', String(id));
    const updated: DirectionItem = { ...bumpEnvelope(existing, this.clock), posture };
    await this.store.put(Collections.directionItems, String(id), updated);
    return updated;
  }

  async archiveItem(id: EntityId): Promise<DirectionItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('DirectionItem', String(id));
    const updated: DirectionItem = { ...bumpEnvelope(existing, this.clock), lifecycle: 'archived' };
    await this.store.put(Collections.directionItems, String(id), updated);
    return updated;
  }
}

export class MemoryHealthRepository implements HealthRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createLog(args: {
    ownerId: string;
    occurredOn: string;
    sleepHours: number;
    energy: number;
    movementMinutes: number;
    note: string;
    privacyClass: HealthLog['privacyClass'];
  }): Promise<HealthLog> {
    const env = createEnvelope({ kind: 'health_log', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const log: HealthLog = {
      ...env,
      kind: 'health_log',
      occurredOn: args.occurredOn,
      sleepHours: args.sleepHours,
      energy: args.energy,
      movementMinutes: args.movementMinutes,
      note: args.note,
    };
    await this.store.put(Collections.healthLogs, String(log.id), log);
    return log;
  }

  async listLogs(): Promise<readonly HealthLog[]> {
    const all = await this.store.list<HealthLog>(Collections.healthLogs);
    return all.filter((l) => l.lifecycle === 'active');
  }
}

export class MemoryStudyRepository implements StudyRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createSubject(args: {
    ownerId: string;
    title: string;
    masteryState: MasteryState;
    privacyClass: StudySubject['privacyClass'];
  }): Promise<StudySubject> {
    const env = createEnvelope({ kind: 'study_subject', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const subject: StudySubject = {
      ...env,
      kind: 'study_subject',
      title: args.title,
      masteryState: args.masteryState,
      status: 'active',
      linkedIds: [],
    };
    await this.store.put(Collections.studySubjects, String(subject.id), subject);
    return subject;
  }

  async getSubject(id: EntityId): Promise<StudySubject | undefined> {
    return this.store.get<StudySubject>(Collections.studySubjects, String(id));
  }

  async listSubjects(): Promise<readonly StudySubject[]> {
    const all = await this.store.list<StudySubject>(Collections.studySubjects);
    return all.filter((s) => s.lifecycle === 'active');
  }

  async setMastery(id: EntityId, state: MasteryState): Promise<StudySubject> {
    const existing = await this.getSubject(id);
    if (!existing) throw new RecordNotFoundError('StudySubject', String(id));
    const updated: StudySubject = { ...bumpEnvelope(existing, this.clock), masteryState: state };
    await this.store.put(Collections.studySubjects, String(id), updated);
    return updated;
  }

  async setSubjectStatus(id: EntityId, status: SubjectStatus): Promise<StudySubject> {
    const existing = await this.getSubject(id);
    if (!existing) throw new RecordNotFoundError('StudySubject', String(id));
    const updated: StudySubject = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.studySubjects, String(id), updated);
    return updated;
  }

  async createSession(args: {
    ownerId: string;
    subjectId: EntityId;
    minutes: number;
    occurredOn: string;
    note: string;
    privacyClass: StudySession['privacyClass'];
  }): Promise<StudySession> {
    const subject = await this.getSubject(args.subjectId);
    if (!subject) throw new RecordNotFoundError('StudySubject', String(args.subjectId));
    const env = createEnvelope({ kind: 'study_session', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const session: StudySession = {
      ...env,
      kind: 'study_session',
      subjectId: args.subjectId,
      minutes: Math.max(0, Math.trunc(args.minutes)),
      occurredOn: args.occurredOn,
      note: args.note,
    };
    await this.store.put(Collections.studySessions, String(session.id), session);
    return session;
  }

  async listSessions(subjectId?: EntityId): Promise<readonly StudySession[]> {
    const all = await this.store.list<StudySession>(Collections.studySessions);
    const active = all.filter((s) => s.lifecycle === 'active');
    if (!subjectId) return active;
    return active.filter((s) => String(s.subjectId) === String(subjectId));
  }
}

export class MemoryCareerRepository implements CareerRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createOpportunity(args: {
    ownerId: string;
    title: string;
    organization: string;
    url: string;
    note: string;
    privacyClass: Opportunity['privacyClass'];
  }): Promise<Opportunity> {
    const env = createEnvelope({ kind: 'opportunity', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const opp: Opportunity = {
      ...env,
      kind: 'opportunity',
      title: args.title,
      organization: args.organization,
      stage: 'saved',
      url: args.url,
      note: args.note,
      linkedIds: [],
    };
    await this.store.put(Collections.opportunities, String(opp.id), opp);
    return opp;
  }

  async getOpportunity(id: EntityId): Promise<Opportunity | undefined> {
    return this.store.get<Opportunity>(Collections.opportunities, String(id));
  }

  async listOpportunities(): Promise<readonly Opportunity[]> {
    const all = await this.store.list<Opportunity>(Collections.opportunities);
    return all.filter((o) => o.lifecycle === 'active');
  }

  async updateOpportunity(id: EntityId, patch: OpportunityPatch): Promise<Opportunity> {
    const existing = await this.getOpportunity(id);
    if (!existing) throw new RecordNotFoundError('Opportunity', String(id));
    const updated: Opportunity = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      organization: patch.organization ?? existing.organization,
      url: patch.url ?? existing.url,
      note: patch.note ?? existing.note,
    };
    await this.store.put(Collections.opportunities, String(id), updated);
    return updated;
  }

  async setStage(id: EntityId, stage: OpportunityStage): Promise<Opportunity> {
    const existing = await this.getOpportunity(id);
    if (!existing) throw new RecordNotFoundError('Opportunity', String(id));
    const updated: Opportunity = { ...bumpEnvelope(existing, this.clock), stage };
    await this.store.put(Collections.opportunities, String(id), updated);
    return updated;
  }
}

export class MemoryJournalRepository implements JournalRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createEntry(args: {
    ownerId: string;
    stream: JournalStream;
    entryKind: JournalKind;
    title: string;
    body: string;
    occurredOn: string;
    privacyClass: JournalEntry['privacyClass'];
  }): Promise<JournalEntry> {
    const env = createEnvelope({ kind: 'journal_entry', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const entry: JournalEntry = {
      ...env,
      kind: 'journal_entry',
      stream: args.stream,
      entryKind: args.entryKind,
      title: args.title,
      body: args.body,
      occurredOn: args.occurredOn,
    };
    await this.store.put(Collections.journalEntries, String(entry.id), entry);
    return entry;
  }

  async listEntries(stream?: JournalStream): Promise<readonly JournalEntry[]> {
    const all = await this.store.list<JournalEntry>(Collections.journalEntries);
    const active = all.filter((e) => e.lifecycle === 'active');
    if (!stream) return active;
    return active.filter((e) => e.stream === stream);
  }
}

export class MemoryContentRepository implements ContentRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createItem(args: {
    ownerId: string;
    title: string;
    platform: string;
    note: string;
    url: string;
    privacyClass: ContentItem['privacyClass'];
  }): Promise<ContentItem> {
    const env = createEnvelope({ kind: 'content_item', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const item: ContentItem = {
      ...env,
      kind: 'content_item',
      title: args.title,
      platform: args.platform,
      stage: 'idea',
      note: args.note,
      url: args.url,
      linkedIds: [],
    };
    await this.store.put(Collections.contentItems, String(item.id), item);
    return item;
  }

  async getItem(id: EntityId): Promise<ContentItem | undefined> {
    return this.store.get<ContentItem>(Collections.contentItems, String(id));
  }

  async listItems(): Promise<readonly ContentItem[]> {
    const all = await this.store.list<ContentItem>(Collections.contentItems);
    return all.filter((c) => c.lifecycle === 'active');
  }

  async updateItem(id: EntityId, patch: ContentItemPatch): Promise<ContentItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('ContentItem', String(id));
    const updated: ContentItem = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      platform: patch.platform ?? existing.platform,
      note: patch.note ?? existing.note,
      url: patch.url ?? existing.url,
    };
    await this.store.put(Collections.contentItems, String(id), updated);
    return updated;
  }

  async setStage(id: EntityId, stage: ContentStage): Promise<ContentItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('ContentItem', String(id));
    const updated: ContentItem = { ...bumpEnvelope(existing, this.clock), stage };
    await this.store.put(Collections.contentItems, String(id), updated);
    return updated;
  }
}

export class MemoryBusinessRepository implements BusinessRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createVenture(args: {
    ownerId: string;
    title: string;
    sector: string;
    monthlyRevenueMinor: number;
    note: string;
    privacyClass: Venture['privacyClass'];
  }): Promise<Venture> {
    const env = createEnvelope({ kind: 'venture', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const venture: Venture = {
      ...env,
      kind: 'venture',
      title: args.title,
      sector: args.sector,
      status: 'idea',
      monthlyRevenueMinor: Math.max(0, Math.trunc(args.monthlyRevenueMinor)),
      note: args.note,
      linkedIds: [],
    };
    await this.store.put(Collections.ventures, String(venture.id), venture);
    return venture;
  }

  async getVenture(id: EntityId): Promise<Venture | undefined> {
    return this.store.get<Venture>(Collections.ventures, String(id));
  }

  async listVentures(): Promise<readonly Venture[]> {
    const all = await this.store.list<Venture>(Collections.ventures);
    return all.filter((v) => v.lifecycle === 'active');
  }

  async updateVenture(id: EntityId, patch: VenturePatch): Promise<Venture> {
    const existing = await this.getVenture(id);
    if (!existing) throw new RecordNotFoundError('Venture', String(id));
    const updated: Venture = {
      ...bumpEnvelope(existing, this.clock),
      title: patch.title ?? existing.title,
      sector: patch.sector ?? existing.sector,
      monthlyRevenueMinor:
        patch.monthlyRevenueMinor !== undefined ? Math.max(0, Math.trunc(patch.monthlyRevenueMinor)) : existing.monthlyRevenueMinor,
      note: patch.note ?? existing.note,
    };
    await this.store.put(Collections.ventures, String(id), updated);
    return updated;
  }

  async setStatus(id: EntityId, status: VentureStatus): Promise<Venture> {
    const existing = await this.getVenture(id);
    if (!existing) throw new RecordNotFoundError('Venture', String(id));
    const updated: Venture = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.ventures, String(id), updated);
    return updated;
  }
}

export class MemoryEnvironmentRepository implements EnvironmentRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createItem(args: {
    ownerId: string;
    name: string;
    category: ItemCategory;
    location: string;
    note: string;
    privacyClass: InventoryItem['privacyClass'];
  }): Promise<InventoryItem> {
    const env = createEnvelope({ kind: 'inventory_item', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const item: InventoryItem = {
      ...env,
      kind: 'inventory_item',
      name: args.name,
      category: args.category,
      status: 'in_use',
      location: args.location,
      note: args.note,
      linkedIds: [],
    };
    await this.store.put(Collections.inventoryItems, String(item.id), item);
    return item;
  }

  async getItem(id: EntityId): Promise<InventoryItem | undefined> {
    return this.store.get<InventoryItem>(Collections.inventoryItems, String(id));
  }

  async listItems(): Promise<readonly InventoryItem[]> {
    const all = await this.store.list<InventoryItem>(Collections.inventoryItems);
    return all.filter((i) => i.lifecycle === 'active');
  }

  async updateItem(id: EntityId, patch: InventoryItemPatch): Promise<InventoryItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('InventoryItem', String(id));
    const updated: InventoryItem = {
      ...bumpEnvelope(existing, this.clock),
      name: patch.name ?? existing.name,
      category: patch.category ?? existing.category,
      location: patch.location ?? existing.location,
      note: patch.note ?? existing.note,
    };
    await this.store.put(Collections.inventoryItems, String(id), updated);
    return updated;
  }

  async setStatus(id: EntityId, status: ItemStatus): Promise<InventoryItem> {
    const existing = await this.getItem(id);
    if (!existing) throw new RecordNotFoundError('InventoryItem', String(id));
    const updated: InventoryItem = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.inventoryItems, String(id), updated);
    return updated;
  }
}

export class MemoryFocusRepository implements FocusRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async createSession(args: {
    ownerId: string;
    minutes: number;
    occurredOn: string;
    note: string;
    linkedId: EntityId | null;
    privacyClass: FocusSession['privacyClass'];
  }): Promise<FocusSession> {
    const env = createEnvelope({ kind: 'focus_session', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const session: FocusSession = {
      ...env,
      kind: 'focus_session',
      minutes: Math.max(0, Math.trunc(args.minutes)),
      occurredOn: args.occurredOn,
      note: args.note,
      linkedId: args.linkedId,
    };
    await this.store.put(Collections.focusSessions, String(session.id), session);
    return session;
  }

  async listSessions(): Promise<readonly FocusSession[]> {
    const all = await this.store.list<FocusSession>(Collections.focusSessions);
    return all.filter((s) => s.lifecycle === 'active');
  }
}

const SECURITY_CONFIG_KEY = 'config';

export class MemorySecurityRepository implements SecurityRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async getConfig(): Promise<SecurityConfig | undefined> {
    return this.store.get<SecurityConfig>(Collections.securityConfig, SECURITY_CONFIG_KEY);
  }

  async putConfig(config: SecurityConfig): Promise<void> {
    await this.store.put(Collections.securityConfig, SECURITY_CONFIG_KEY, config);
  }

  async appendEvent(args: {
    ownerId: string;
    eventType: SecurityEventType;
    at: string;
    detail: string;
  }): Promise<SecurityEvent> {
    // Security events are strictly local and never leave the device.
    const env = createEnvelope({ kind: 'security_event', ownerId: args.ownerId, privacyClass: 'strict_local' }, this.clock);
    const event: SecurityEvent = {
      ...env,
      kind: 'security_event',
      eventType: args.eventType,
      at: args.at,
      detail: args.detail,
    };
    await this.store.put(Collections.securityEvents, String(event.id), event);
    return event;
  }

  async listEvents(limit: number): Promise<readonly SecurityEvent[]> {
    const all = await this.store.list<SecurityEvent>(Collections.securityEvents);
    return [...all].sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);
  }
}


export class MemoryAcademicsRepository implements AcademicsRepository {
  constructor(private readonly store: LocalStore, private readonly clock: Clock) {}

  async upsertProfile(args: {
    ownerId: string;
    displayName: string;
    headline: string;
    location: string;
    education: readonly EducationRecord[];
    links: readonly { readonly label: string; readonly url: string }[];
    privacyClass: Profile['privacyClass'];
  }): Promise<Profile> {
    const existing = await this.getProfile();
    if (existing) {
      const updated: Profile = {
        ...bumpEnvelope(existing, this.clock),
        displayName: args.displayName,
        headline: args.headline,
        location: args.location,
        education: args.education,
        links: args.links,
      };
      await this.store.put(Collections.profiles, String(updated.id), updated);
      return updated;
    }
    const env = createEnvelope({ kind: 'profile', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const profile: Profile = {
      ...env,
      kind: 'profile',
      displayName: args.displayName,
      headline: args.headline,
      location: args.location,
      education: args.education,
      links: args.links,
      linkedIds: [],
    };
    await this.store.put(Collections.profiles, String(profile.id), profile);
    return profile;
  }

  async getProfile(): Promise<Profile | undefined> {
    const all = await this.store.list<Profile>(Collections.profiles);
    return all.find((p) => p.lifecycle === 'active') ?? all[0];
  }

  async createAdmission(args: {
    ownerId: string;
    institution: string;
    programme: string;
    offerStatus: OfferStatus;
    conditions: readonly string[];
    currency: string;
    tuitionMinor: number;
    depositMinor: number;
    livingEstimateMinor: number;
    depositDueDate: string | null;
    decisionDate: string | null;
    intakeTerm: string;
    note: string;
    privacyClass: Admission['privacyClass'];
  }): Promise<Admission> {
    const env = createEnvelope({ kind: 'admission', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const admission: Admission = {
      ...env,
      kind: 'admission',
      institution: args.institution,
      programme: args.programme,
      offerStatus: args.offerStatus,
      conditions: args.conditions,
      currency: args.currency,
      tuitionMinor: Math.max(0, Math.trunc(args.tuitionMinor)),
      depositMinor: Math.max(0, Math.trunc(args.depositMinor)),
      livingEstimateMinor: Math.max(0, Math.trunc(args.livingEstimateMinor)),
      depositDueDate: args.depositDueDate,
      decisionDate: args.decisionDate,
      intakeTerm: args.intakeTerm,
      status: 'open',
      note: args.note,
      linkedIds: [],
    };
    await this.store.put(Collections.admissions, String(admission.id), admission);
    return admission;
  }

  async getAdmission(id: EntityId): Promise<Admission | undefined> {
    return this.store.get<Admission>(Collections.admissions, String(id));
  }

  async listAdmissions(): Promise<readonly Admission[]> {
    const all = await this.store.list<Admission>(Collections.admissions);
    return all.filter((a) => a.lifecycle === 'active');
  }

  async setOfferStatus(id: EntityId, offerStatus: OfferStatus): Promise<Admission> {
    const existing = await this.getAdmission(id);
    if (!existing) throw new RecordNotFoundError('Admission', String(id));
    const updated: Admission = { ...bumpEnvelope(existing, this.clock), offerStatus };
    await this.store.put(Collections.admissions, String(id), updated);
    return updated;
  }

  async setAdmissionStatus(id: EntityId, status: AdmissionStatus): Promise<Admission> {
    const existing = await this.getAdmission(id);
    if (!existing) throw new RecordNotFoundError('Admission', String(id));
    const updated: Admission = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.admissions, String(id), updated);
    return updated;
  }

  async createFunding(args: {
    ownerId: string;
    name: string;
    sourceKind: FundingKind;
    status: FundingStatus;
    currency: string;
    amountMinor: number;
    fullyFunded: boolean;
    opensOn: string | null;
    closesOn: string | null;
    note: string;
    nextAction: string;
    nextActionDate: string | null;
    privacyClass: FundingSource['privacyClass'];
  }): Promise<FundingSource> {
    const env = createEnvelope({ kind: 'funding_source', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const funding: FundingSource = {
      ...env,
      kind: 'funding_source',
      name: args.name,
      sourceKind: args.sourceKind,
      status: args.status,
      currency: args.currency,
      amountMinor: Math.max(0, Math.trunc(args.amountMinor)),
      fullyFunded: args.fullyFunded,
      opensOn: args.opensOn,
      closesOn: args.closesOn,
      note: args.note,
      nextAction: args.nextAction,
      nextActionDate: args.nextActionDate,
      touchpoints: [],
      linkedIds: [],
    };
    await this.store.put(Collections.fundingSources, String(funding.id), funding);
    return funding;
  }

  async getFunding(id: EntityId): Promise<FundingSource | undefined> {
    return this.store.get<FundingSource>(Collections.fundingSources, String(id));
  }

  async listFunding(): Promise<readonly FundingSource[]> {
    const all = await this.store.list<FundingSource>(Collections.fundingSources);
    return all.filter((f) => f.lifecycle === 'active');
  }

  async setFundingStatus(id: EntityId, status: FundingStatus): Promise<FundingSource> {
    const existing = await this.getFunding(id);
    if (!existing) throw new RecordNotFoundError('FundingSource', String(id));
    const updated: FundingSource = { ...bumpEnvelope(existing, this.clock), status };
    await this.store.put(Collections.fundingSources, String(id), updated);
    return updated;
  }

  async setFundingNextAction(id: EntityId, nextAction: string, nextActionDate: string | null): Promise<FundingSource> {
    const existing = await this.getFunding(id);
    if (!existing) throw new RecordNotFoundError('FundingSource', String(id));
    const updated: FundingSource = { ...bumpEnvelope(existing, this.clock), nextAction, nextActionDate };
    await this.store.put(Collections.fundingSources, String(id), updated);
    return updated;
  }

  async logTouchpoint(id: EntityId, touchpoint: { on: string; channel: TouchpointChannel; note: string }): Promise<FundingSource> {
    const existing = await this.getFunding(id);
    if (!existing) throw new RecordNotFoundError('FundingSource', String(id));
    const entry: Touchpoint = { on: touchpoint.on, channel: touchpoint.channel, note: touchpoint.note };
    // Newest first; keep history immutable + ordered.
    const touchpoints: readonly Touchpoint[] = [entry, ...existing.touchpoints];
    const updated: FundingSource = { ...bumpEnvelope(existing, this.clock), touchpoints };
    await this.store.put(Collections.fundingSources, String(id), updated);
    return updated;
  }

  async upsertEducationProgram(args: {
    ownerId: string;
    program: EducationProgramInput;
    privacyClass: EducationProgram['privacyClass'];
  }): Promise<EducationProgram> {
    const p = args.program;
    // Idempotent by registration number (or title when regNo is absent).
    const all = await this.store.list<EducationProgram>(Collections.educationPrograms);
    const match = all.find(
      (e) =>
        e.lifecycle === 'active' &&
        ((p.registrationNumber && e.registrationNumber === p.registrationNumber) ||
          (!p.registrationNumber && e.title === p.title)),
    );
    const body = {
      institution: p.institution,
      qualification: p.qualification,
      title: p.title,
      field: p.field ?? null,
      branch: p.branch ?? null,
      school: p.school ?? null,
      registrationNumber: p.registrationNumber ?? null,
      institutionEmail: p.institutionEmail ?? null,
      city: p.city ?? null,
      country: p.country ?? null,
      website: p.website ?? null,
      startDate: p.startDate ?? null,
      endDate: p.endDate ?? null,
      totalCredits: p.totalCredits ?? null,
      cgpa: p.cgpa ?? null,
      gradeLabel: p.gradeLabel ?? null,
      status: p.status ?? 'completed',
      creditSummary: p.creditSummary ?? [],
      courses: p.courses ?? [],
    };
    if (match) {
      const updated: EducationProgram = { ...bumpEnvelope(match, this.clock), ...body };
      await this.store.put(Collections.educationPrograms, String(match.id), updated);
      return updated;
    }
    const env = createEnvelope({ kind: 'education_program', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const program: EducationProgram = { ...env, kind: 'education_program', ...body, linkedIds: [] };
    await this.store.put(Collections.educationPrograms, String(program.id), program);
    return program;
  }

  async listEducationPrograms(): Promise<readonly EducationProgram[]> {
    const all = await this.store.list<EducationProgram>(Collections.educationPrograms);
    return all.filter((e) => e.lifecycle === 'active');
  }

  async upsertResumeProfile(args: {
    ownerId: string;
    profile: ResumeProfileInput;
    privacyClass: ResumeProfile['privacyClass'];
  }): Promise<ResumeProfile> {
    const p = args.profile;
    const existing = await this.getResumeProfile();
    const body = {
      targetRole: p.targetRole ?? null,
      seniority: p.seniority ?? null,
      marketFocus: p.marketFocus ?? null,
      summary: p.summary ?? null,
      strengths: p.strengths ?? null,
      gaps: p.gaps ?? null,
      goals: p.goals ?? null,
      philosophy: p.philosophy ?? null,
      lifestyle: p.lifestyle ?? null,
      contact: {
        email: p.contact?.email ?? null,
        phone: p.contact?.phone ?? null,
        github: p.contact?.github ?? null,
        linkedin: p.contact?.linkedin ?? null,
        portfolio: p.contact?.portfolio ?? null,
      },
      skills: p.skills ?? [],
      experience: p.experience ?? [],
      projects: p.projects ?? [],
      credentials: p.credentials ?? [],
      recognitions: p.recognitions ?? [],
    };
    if (existing) {
      const updated: ResumeProfile = { ...bumpEnvelope(existing, this.clock), ...body };
      await this.store.put(Collections.resumeProfiles, String(existing.id), updated);
      return updated;
    }
    const env = createEnvelope({ kind: 'resume_profile', ownerId: args.ownerId, privacyClass: args.privacyClass }, this.clock);
    const profile: ResumeProfile = { ...env, kind: 'resume_profile', ...body, linkedIds: [] };
    await this.store.put(Collections.resumeProfiles, String(profile.id), profile);
    return profile;
  }

  async getResumeProfile(): Promise<ResumeProfile | undefined> {
    const all = await this.store.list<ResumeProfile>(Collections.resumeProfiles);
    return all.filter((r) => r.lifecycle === 'active')[0];
  }
}
